import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  descargarComoXlsx,
  dispararExport,
  exportJobId,
  getExportLatest,
} from '@lib/services/exportaciones.service';

/** Si el job no termina en este tiempo, cortamos el poll y mostramos error. */
const DEADLINE_MS = 8 * 60 * 1000;

/** El `message` que mandó el backend, si lo hay. Nest lo pone en `message`. */
function mensajeDeApi(err: unknown): string | null {
  const data = (err as { response?: { data?: { message?: unknown } } } | undefined)?.response?.data;
  const m = data?.message;
  if (typeof m === 'string' && m.trim()) return m;
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0];
  return null;
}

/**
 * Modal genérico de descarga de exports grandes. Dispara el job, muestra
 * progreso, y apenas termina baja el CSV y lo convierte a .xlsx EN EL CLIENTE.
 * La descarga nunca falla por timeout (el archivo se genera en background) y se
 * borra del server al bajarlo.
 *
 * Solo procesamos el job que ESTE modal disparó (match por id): el poll de
 * "último job" puede devolver uno viejo/borrado antes de que el dispara cree el
 * nuevo, y bajar ese da 404. Un deadline corta el poll si el job nunca llega a
 * estado terminal (worker caído).
 */
export function ExportModal({
  tipo,
  params = {},
  titulo,
  onClose,
}: {
  tipo: string;
  params?: Record<string, string>;
  titulo: string;
  onClose: () => void;
}) {
  const [convirtiendo, setConvirtiendo] = useState(false);
  const [listo, setListo] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);
  const [vencio, setVencio] = useState(false);
  const [intento, setIntento] = useState(0);
  const procesadoRef = useRef(false);
  const disparado = useRef(false);

  const dispara = useMutation({ mutationFn: () => dispararExport(tipo, params) });

  useEffect(() => {
    if (disparado.current) return;
    disparado.current = true;
    dispara.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deadline: se re-arma en cada intento (mount + "Generar de nuevo").
  useEffect(() => {
    setVencio(false);
    const t = setTimeout(() => setVencio(true), DEADLINE_MS);
    return () => clearTimeout(t);
  }, [intento]);

  const ourId = dispara.data ? exportJobId(dispara.data) : null;
  /**
   * El server decidió que este universo es demasiado grande para esperarlo acá:
   * el job sigue solo y el link llega por mail. No hay nada que pollear ni que
   * convertir, así que el modal se limita a avisar. Viene en la respuesta del
   * disparo, así que se sabe desde el primer momento.
   */
  const porMail = dispara.data?.entrega === 'mail';
  const destinatario = dispara.data?.notificarA;

  const q = useQuery({
    queryKey: ['export-latest', tipo, params, ourId, intento],
    queryFn: () => getExportLatest(tipo, params),
    refetchInterval: (query) => {
      if (vencio) return false;
      const dato = query.state.data;
      // Los de mail sí se siguen mirando, pero despacio y con otro objetivo: no
      // para bajar nada sino para poder avisar si el mail NO salió. Si no, un
      // envío fallido quedaba mudo (R7.7: nada en silencio).
      if (porMail) {
        if (dato && (dato.mailEnviadoAt || dato.mailError)) return false;
        return 10_000;
      }
      const d = query.state.data;
      const esNuestro = !!ourId && !!d && exportJobId(d) === ourId;
      if (esNuestro && d.estado !== 'corriendo') return false;
      return 2000;
    },
  });
  const job = ourId && q.data && exportJobId(q.data) === ourId ? q.data : null;

  useEffect(() => {
    // Los de mail no se bajan acá: el archivo sale por el link, y convertirlo en
    // el navegador es justamente lo que no entra (son cientos de miles de filas).
    if (porMail) return;
    if (job?.estado === 'terminado' && !procesadoRef.current) {
      procesadoRef.current = true;
      setConvirtiendo(true);
      descargarComoXlsx(exportJobId(job), job.filename)
        .then(() => {
          setListo(true);
          setConvirtiendo(false);
        })
        .catch((e: unknown) => {
          setErrorDescarga(e instanceof Error ? e.message : 'No se pudo armar el Excel.');
          setConvirtiendo(false);
        });
    }
  }, [job, porMail]);

  const errorDuro = dispara.isError || job?.estado === 'error' || Boolean(errorDescarga);
  // El deadline no aplica cuando la entrega es por mail: ahí tardar es lo
  // esperado, no un síntoma. Si no, a los 8 minutos aparecía un error falso.
  const hayError = errorDuro || (vencio && !listo && !porMail);
  const pct =
    job && job.total > 0 ? Math.min(100, Math.round((job.procesados / job.total) * 100)) : null;
  // El server puede rechazar el pedido con un motivo accionable (por ejemplo,
  // que el usuario no tenga una dirección de mail cargada y el reporte sea
  // demasiado grande para bajarlo en el momento). Ese texto vale mucho más que
  // "probá de nuevo", que además sería mentira: reintentar no lo va a arreglar.
  const motivoDelServer = mensajeDeApi(dispara.error);
  const mensajeError = errorDescarga
    ? errorDescarga
    : (motivoDelServer ??
      (errorDuro
        ? 'No pudimos generar el archivo. Probá de nuevo.'
        : 'La generación está tardando más de lo esperado. Probá de nuevo.'));

  function regenerar() {
    procesadoRef.current = false;
    setListo(false);
    setConvirtiendo(false);
    setErrorDescarga(null);
    // reset() limpia dispara.data → ourId queda null hasta que resuelva el nuevo
    // disparo, así el poll no matchea (ni re-descarga) el job viejo ya borrado.
    dispara.reset();
    setIntento((i) => i + 1);
    dispara.mutate();
  }

  return (
    <Overlay onClose={onClose}>
      <div
        className="card"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(460px, 100%)', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <header className="rowBetween" style={{ alignItems: 'baseline' }}>
          <h3 className="heading-5" style={{ margin: 0 }}>
            {titulo}
          </h3>
          <button type="button" className="btn btn-ghost btn--sm" onClick={onClose}>
            Cerrar
          </button>
        </header>

        <ExportModalEstado
          listo={listo}
          hayError={hayError}
          convirtiendo={convirtiendo}
          mensajeError={mensajeError}
          pct={pct}
          procesados={job?.procesados}
          total={job?.total}
          porMail={porMail}
          destinatario={destinatario}
          filas={dispara.data?.total}
          mailEnviado={Boolean(q.data?.mailEnviadoAt)}
          mailError={q.data?.mailError}
          onClose={onClose}
          regenerar={regenerar}
        />
      </div>
    </Overlay>
  );
}

/**
 * Cuerpo condicional del modal (descargado / error / en progreso). Extraído de
 * `ExportModal` para mantener su complejidad cognitiva ≤ 15 (S3776).
 */
function ExportModalEstado({
  listo,
  hayError,
  convirtiendo,
  mensajeError,
  pct,
  procesados,
  total,
  porMail,
  destinatario,
  filas,
  mailEnviado,
  mailError,
  onClose,
  regenerar,
}: {
  listo: boolean;
  hayError: boolean;
  convirtiendo: boolean;
  mensajeError: string;
  pct: number | null;
  procesados?: number;
  total?: number;
  porMail: boolean;
  destinatario?: string;
  filas?: number;
  mailEnviado: boolean;
  mailError?: string;
  onClose: () => void;
  regenerar: () => void;
}) {
  // Va primero: si es por mail no hay descarga ni progreso que mostrar, y el
  // error por deadline ya quedó desactivado en el padre.
  if (porMail && !hayError) {
    return (
      <>
        {mailError ? (
          <p className="status status-error" style={{ display: 'block', margin: 0, lineHeight: 1.55 }}>
            El archivo se generó, pero <strong>no pudimos mandarte el mail</strong> ({mailError}).
            Avisale a sistemas con este dato; el reporte no se perdió.
          </p>
        ) : (
          <p className="status status-success" style={{ display: 'block', margin: 0 }}>
            {mailEnviado ? '✓ Te lo mandamos por mail' : '✓ Lo estamos generando'}
          </p>
        )}
        {!mailError && (
          <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55 }}>
            Son <strong>{(filas ?? 0).toLocaleString('es-AR')} registros</strong>: demasiados para
            esperar en pantalla.{' '}
            {mailEnviado ? 'Ya te mandamos el link' : 'Cuando esté te mandamos un link'}
            {destinatario ? (
              <>
                {' '}
                a <strong>{destinatario}</strong>
              </>
            ) : null}
            . Podés cerrar esta ventana y seguir trabajando.
          </p>
        )}
        <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55, opacity: 0.8 }}>
          El link es de un solo uso y vence a las 48 horas.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Entendido
          </button>
        </div>
      </>
    );
  }
  if (listo) {
    return (
      <>
        <p className="status status-success" style={{ display: 'block', margin: 0 }}>
          ✓ Tu Excel se descargó. Por seguridad el archivo se elimina del servidor; si lo
          necesitás de nuevo, generá otro.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={regenerar}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={15} /> Generar de nuevo
          </button>
        </div>
      </>
    );
  }
  if (hayError) {
    return (
      <>
        <p className="status status-error" style={{ display: 'block', margin: 0 }}>
          {mensajeError}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={regenerar}>
            Reintentar
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <p className="text-element-mid" style={{ margin: 0, lineHeight: 1.55 }}>
        {convirtiendo ? (
          'Armando el Excel en tu equipo…'
        ) : (
          <>
            Estamos generando tu archivo. <strong>Esto puede tardar unos minutos</strong> según la
            cantidad de registros.
          </>
        )}
      </p>
      <BarraProgreso pct={convirtiendo ? 100 : pct} procesados={procesados} total={total} />
    </>
  );
}

function BarraProgreso({
  pct,
  procesados,
  total,
}: {
  pct: number | null;
  procesados?: number;
  total?: number;
}) {
  return (
    <div>
      <div
        style={{
          height: 8,
          background: 'var(--color-bg-subtle, #eef1f5)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct ?? 0}%`,
            height: '100%',
            background: 'var(--color-primary-darker)',
            transition: 'width .3s ease',
          }}
        />
      </div>
      <p className="text-element-mid" style={{ fontSize: 12, marginTop: 6 }}>
        {pct !== null ? `${procesados ?? 0} / ${total ?? 0} (${pct}%)` : 'Preparando…'}
      </p>
    </div>
  );
}

function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 100,
      }}
      onClick={(e) => {
        // El modal puede vivir dentro de un contenedor clickeable (ej. la fila
        // del funnel); cortamos la propagación para no disparar su onClick.
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}
